import { getItemImage } from '@/shared/utils/itemImages';
import * as v2 from '@/core/services/api/v2';

/**
 * O envelope que as mutations desta store esperam.
 *
 * `SET_RODS` e as irmãs leem `response.data`, herança do Strapi, que embrulhava
 * toda coleção em `{ data: [...] }`. O V2 devolve o array direto, então o
 * embrulho é refeito aqui em vez de mexer em cinco mutations e nas telas que
 * dependem delas — a migração das stores para o vocabulário do V2 é outro passo.
 */
const comoColecao = (itens) => ({ data: itens });

const shopService = {
  async fetchRods() {
    return { data: comoColecao(await v2.catalogo('rod')) };
  },
  async fetchBaits() {
    return { data: comoColecao(await v2.catalogo('bait')) };
  },
  async fetchBoats() {
    return { data: comoColecao(await v2.catalogo('boat')) };
  },
  async fetchHouses() {
    return { data: comoColecao(await v2.catalogo('house')) };
  },
  /**
   * Preço de peixe, agora do bestiário do V2 (`GET /fishes`): o valor base por
   * espécie, achatado na lista `[{ name, value }]` que `SET_FISH_MARKET` lê. O
   * valor real de venda continua sendo por instância (peso) — isto é a tabela
   * de referência da tela, não o caixa.
   */
  async fetchMarket() {
    const porMapa = await v2.todosOsPeixes();
    const vistos = new Map();
    for (const lista of Object.values(porMapa)) {
      for (const peixe of lista) {
        vistos.set(peixe.name, { name: peixe.name, value: peixe.value });
      }
    }
    return { data: [...vistos.values()] };
  },
  /** O catálogo premium do V2, no envelope duplo que `fetchShopItems` extrai. */
  async fetchPremiumItems() {
    return { data: { data: await v2.itensPremium() } };
  }
};

/**
 * Comprar, no V2.
 *
 * Uma rota só para as quatro categorias (`POST /player/:id/shop/buy`), e o corpo
 * diz **o que**, nunca **por quanto**: o preço sai do catálogo do servidor. As
 * rotas antigas recebiam `price` do cliente.
 *
 * Depois da compra o jogador é recarregado inteiro. A resposta traz o ouro
 * restante, mas não o item novo no inventário — e mostrar o saldo certo com a
 * vara faltando é pior do que uma ida a mais ao servidor numa ação que ninguém
 * repete trinta vezes por minuto.
 */
async function comprarNaLoja({ dispatch, rootState, state }, categoria, itemId, quantidade = 1) {
  const playerId = rootState.player?.id;
  if (!playerId) {
    return { success: false, error: 'Jogador não carregado' };
  }

  try {
    const resultado = await v2.comprar(playerId, { categoria, itemId, quantidade });
    await dispatch('player/loadPlayerState', playerId, { root: true });

    // O bilhete de "compra feita" (03/09): a confirmação vive aqui, no único caminho de compra,
    // e não em cada uma das seis lojas. O nome sai do catálogo já carregado nesta store.
    const catalogoDaCategoria = { rod: 'rods', bait: 'baits', boat: 'boats', house: 'houses' };
    const item = (state[catalogoDaCategoria[categoria]] || []).find((i) => i.id === itemId);
    dispatch(
      'avisos/avisar',
      {
        chave: quantidade > 1 ? 'purchase.aviso.varios' : 'purchase.aviso.um',
        parametros: { name: item?.name || '', qty: quantidade },
        icone: 'coins',
      },
      { root: true },
    );

    return { success: true, ...resultado };
  } catch (error) {
    // O V2 responde `{ message }`; o Strapi respondia `{ error: { message } }`.
    const mensagem =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      error.message;
    console.error(`Erro ao comprar ${categoria}:`, mensagem);
    return { success: false, error: mensagem };
  }
}

const state = {
  rods: [],
  baits: [],
  boats: [],
  houses: [],
  fishMarket: [],
  availableRods: [],
  availableBoats: [],
  fishInventory: [],
  currentPrices: {},
  shopItems: [],
  premiumItems: [], // Add this
  isLoading: false,
  error: null
};

const mutations = {
  SET_RODS(state, response) {
    if (!response?.data) return;
    state.rods = response.data.map(rod => ({
      id: rod.id,
      name: rod.name || 'Vara sem nome',
      price: rod.price || 0,
      image: getItemImage(rod.name),
      power: rod.power || 1,
      requiredLevel: rod.requiredLevel || 1,
      description: rod.description || '',
      type: 'rod',
      stackable: false,
      durability: rod.durability,
      /*
       * A raridade se perdia AQUI.
       *
       * Esta mutation reconstrói o objeto campo a campo — ao contrário de `SET_BAITS` e
       * `SET_BOATS`, que espalham o item inteiro — e `rarity` não estava na lista. O servidor
       * mandava, o adapter passava, e a loja jogava fora: toda vara aparecia como comum, da
       * Junco à Vara do Destino, com o mesmo aro cinza no encaixe (visto na tela em 09/09/2026).
       */
      rarity: rod.rarity,
    }));
  },
  SET_BAITS(state, response) {
    if (!response?.data) return;
    state.baits = response.data.map(bait => ({
      id: bait.id,
      ...bait,
      image: getItemImage(bait.name),
      fishingPower: bait.fishingPower || 1,
      requiredLevel: bait.requiredLevel || 0
    }));
  },
  SET_BOATS(state, response) {
    if (!response?.data) return;
    state.boats = response.data.map(boat => ({
      id: boat.id,
      ...boat,
      image: getItemImage(boat.name),
      speed: boat.speed || 1,
      requiredLevel: boat.requiredLevel || 1
    }));
  },
  SET_HOUSES(state, response) {
    if (!response?.data) return;
    state.houses = response.data.map(house => ({
      id: house.id,
      name: house.name || 'Casa sem nome',
      price: house.price || 0,
      // A casa era a única da loja presa ao placeholder desde jan/2025: as seis fotos existem
      // em `public/images/houses/` e `itemImages.js` as conhece. Quem não tem casa (toda conta
      // nova) só vê casa aqui — e via o quadrado genérico.
      image: getItemImage(house.name) || require('@/assets/images/default-item.svg'),
      rechargeStamina: house.rechargeStamina || 1,
      requiredLevel: house.requiredLevel || 1,
      description: house.description || '',
      rarity: house.rarity || 'common',
      type: 'house',
      stackable: false
    }));
  },
  SET_FISH_MARKET(state, response) {
    if (!response?.data) return;
    const prices = {};
    response.data.forEach(fish => {
      prices[fish.name] = {
        basePrice: fish.value,
        currentPrice: fish.value, // Por enquanto usa o mesmo valor base
        variation: 0 // Implementar variação depois
      };
    });
    state.currentPrices = prices;
  },
  SET_PREMIUM_ITEMS(state, items) {
    state.premiumItems = items;
  },
  SHOP_SET_LOADING(state, loading) {
    state.isLoading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_SHOP_ITEMS(state, items) {
    state.shopItems = items;
  }
};

const actions = {
  async fetchRods({ commit, state }) {
    if (state.rods.length > 0) return; // Skip if already loaded
    commit('SHOP_SET_LOADING', true);
    try {
      const { data } = await shopService.fetchRods();
      commit('SET_RODS', data);
    } catch (error) {
      console.error('Error fetching rods:', error);
      commit('SET_ERROR', error.message);
    } finally {
      commit('SHOP_SET_LOADING', false);
    }
  },

  async fetchBaits({ commit, state }) {
    if (state.baits.length > 0) return; // Skip if already loaded
    commit('SHOP_SET_LOADING', true);
    try {
      const { data } = await shopService.fetchBaits();
      commit('SET_BAITS', data);
    } catch (error) {
      console.error('Error fetching baits:', error);
      commit('SET_ERROR', error.message);
    } finally {
      commit('SHOP_SET_LOADING', false);
    }
  },

  async fetchBoats({ commit, state }) {
    if (state.boats.length > 0) return; // Skip if already loaded
    commit('SHOP_SET_LOADING', true);
    try {
      const { data } = await shopService.fetchBoats();
      commit('SET_BOATS', data);
    } catch (error) {
      console.error('Error fetching boats:', error);
      commit('SET_ERROR', error.message);
    } finally {
      commit('SHOP_SET_LOADING', false);
    }
  },

  async fetchFishMarket({ commit, state }) {
    if (Object.keys(state.currentPrices).length > 0) return; // Skip if already loaded
    commit('SHOP_SET_LOADING', true);
    try {
      const { data } = await shopService.fetchMarket();
      commit('SET_FISH_MARKET', data);
    } catch (error) {
      console.error('Error fetching fish market:', error);
      commit('SET_ERROR', error.message);
    } finally {
      commit('SHOP_SET_LOADING', false);
    }
  },
  async fetchHouses({ commit, state }) {
    if (state.houses.length > 0) return; // Skip if already loaded
    commit('SHOP_SET_LOADING', true);
    try {
      const { data } = await shopService.fetchHouses();
      commit('SET_HOUSES', data);
    } catch (error) {
      console.error('Error fetching houses:', error);
      commit('SET_ERROR', error.message);
    } finally {
      commit('SHOP_SET_LOADING', false);
    }
  },

  async purchaseHouse(contexto, { houseId }) {
    return comprarNaLoja(contexto, 'house', houseId);
  },

  async fetchPremiumItems({ commit, state }) {
    if (state.premiumItems.length > 0) return; // Skip if already loaded
    commit('SHOP_SET_LOADING', true);
    try {
      // O serviço devolve o envelope duplo `{ data: { data: [...] } }` (ver o comentário nele).
      // A versão anterior desta action commitava o envelope inteiro — foi por isso que ela viveu
      // comentada e o PremiumShop quebrava com "unknown action type" ao abrir.
      const { data } = await shopService.fetchPremiumItems();
      commit('SET_PREMIUM_ITEMS', data.data || []);
    } catch (error) {
      console.error('Error fetching premium items:', error);
      commit('SET_ERROR', error.message);
    } finally {
      commit('SHOP_SET_LOADING', false);
    }
  },

  /**
   * Carrega a loja inteira, no boot.
   *
   * `allSettled` e não `all`, e a diferença é o que faz a loja abrir: mercado de
   * peixe e itens premium ainda apontam para o Strapi, que está fora. Com
   * `Promise.all`, a primeira dessas rejeições derrubaria também varas, iscas,
   * barcos e casas — que vêm do V2 e funcionam. Cada parte que chega é
   * comprometida; o que falta falta sozinho.
   */
  async fetchShopItems({ commit }) {
    commit('SHOP_SET_LOADING', true);

    const partes = [
      { nome: 'rods', mutation: 'SET_RODS', buscar: () => shopService.fetchRods() },
      { nome: 'baits', mutation: 'SET_BAITS', buscar: () => shopService.fetchBaits() },
      { nome: 'boats', mutation: 'SET_BOATS', buscar: () => shopService.fetchBoats() },
      { nome: 'houses', mutation: 'SET_HOUSES', buscar: () => shopService.fetchHouses() },
      { nome: 'market', mutation: 'SET_FISH_MARKET', buscar: () => shopService.fetchMarket() },
      {
        nome: 'premium',
        mutation: 'SET_PREMIUM_ITEMS',
        buscar: () => shopService.fetchPremiumItems(),
        extrair: (resposta) => resposta.data?.data || []
      }
    ];

    const resultados = await Promise.allSettled(partes.map((p) => p.buscar()));

    resultados.forEach((resultado, i) => {
      const parte = partes[i];
      if (resultado.status === 'fulfilled') {
        const valor = parte.extrair ? parte.extrair(resultado.value) : resultado.value.data;
        commit(parte.mutation, valor);
      } else {
        console.warn(`Loja: falhou ao carregar ${parte.nome}:`, resultado.reason?.message);
      }
    });

    commit('SHOP_SET_LOADING', false);
  },

  // Adicionar um método para forçar o recarregamento quando necessário
  async forceRefreshShopItems({ dispatch }) {
    await Promise.all([
      dispatch('fetchRods'),
      dispatch('fetchBaits'),
      dispatch('fetchBoats'),
      dispatch('fetchFishMarket')
    ]);
  },

  // `price` continua na assinatura porque as telas ainda o passam; ele é
  // ignorado de propósito — quem diz o preço é o catálogo do servidor.
  async purchaseRod(contexto, { rodId }) {
    return comprarNaLoja(contexto, 'rod', rodId);
  },

  /** Devolve booleano, e não `{ success }`: é o que as telas de isca esperam. */
  async purchaseBait(contexto, { bait, quantity }) {
    const resultado = await comprarNaLoja(contexto, 'bait', bait.id, quantity);
    return resultado.success;
  },

  async purchaseBoat(contexto, { boatId }) {
    return comprarNaLoja(contexto, 'boat', boatId);
  },

  // `shop/sellFish` saiu (05/09/2026): apontava para `shop/sell-fish` do Strapi
  // e ninguém a chamava. Vender é `player/sellFish`, uma rota só no V2.

  /**
   * Compra premium no V2. O débito é do servidor: o `useParalleliums`
   * otimista saiu das telas — o saldo verdadeiro chega no `loadPlayerState`.
   */
  async purchasePremiumItem({ dispatch, rootState }, item) {
    const playerId = rootState.player?.id;
    if (!playerId) return false;
    try {
      await v2.comprarPremium(playerId, item.id);
      await dispatch('player/loadPlayerState', playerId, { root: true });
      dispatch(
        'avisos/avisar',
        { chave: 'purchase.aviso.um', parametros: { name: item.name || '' }, icone: 'gem' },
        { root: true },
      );
      return true;
    } catch (error) {
      const mensagem =
        error.response?.data?.message || error.response?.data?.error?.message || error.message;
      console.error('Error purchasing premium item:', mensagem);
      return false;
    }
  },

  /**
   * Efeito de evento nos preços, do V2: uma rota só (`GET /events/effects`)
   * responde os três tipos — o Strapi exigia uma chamada por tipo.
   */
  async calculateBaitPriceEffect() {
    try {
      const efeitos = await v2.efeitosDeEvento();
      return efeitos.morePricesBait || 0;
    } catch (error) {
      console.error('Error calculating bait price effect:', error);
      return 0;
    }
  },

  async calculateRodPriceEffect() {
    try {
      const efeitos = await v2.efeitosDeEvento();
      return efeitos.morePricesRod || 0;
    } catch (error) {
      console.error('Error calculating rod price effect:', error);
      return 0;
    }
  },

  async calculateBoatPriceEffect() {
    try {
      const efeitos = await v2.efeitosDeEvento();
      return efeitos.morePricesBoat || 0;
    } catch (error) {
      console.error('Error calculating boat price effect:', error);
      return 0;
    }
  }
};

const getters = {
  availableRods: (state) => state.rods,
  availableBaits: (state) => state.baits,
  availableBoats: (state) => state.boats,
  availableHouses: state => state.houses,
  fishMarketPrices: state => state.currentPrices,
  getPremiumItems: state => state.premiumItems,
  getRodById: (state) => (id) => state.rods.find(rod => rod.id === id),
  getBaitById: (state) => (id) => state.baits.find(bait => bait.id === id),
  getBoatById: (state) => (id) => state.boats.find(boat => boat.id === id),
  getHouseById: (state) => (id) => state.houses.find(house => house.id === id),
  getRodsByLevel: (state) => (level) => state.rods.filter(rod => rod.level <= level),
  getBaitsByType: (state) => (type) => state.baits.filter(bait => bait.type === type),
  getBoatsByLevel: (state) => (level) => state.boats.filter(boat => boat.level <= level),
  isLoading: state => state.isLoading,
  error: state => state.error
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};